export type CampaignVideoProvider = 'youtube' | 'vimeo' | 'instagram';

export type CampaignVideoSource =
  | { kind: 'file'; src: string }
  | { kind: 'embed'; src: string; provider: CampaignVideoProvider }
  | { kind: 'unsupported'; reason: string };

const FILE_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.ogv', '.m4v', '.mov'];

const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com'];
const VIMEO_HOSTS = ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'];
const INSTAGRAM_HOSTS = ['instagram.com', 'www.instagram.com'];

export const UNSUPPORTED_VIDEO_REASON =
  'Paste a direct video file (.mp4, .webm) or a YouTube, Vimeo or Instagram link.';

function youtubeId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const id = raw.trim();
  return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
}

function segmentAfter(segments: string[], prefix: string): string | null {
  const index = segments.indexOf(prefix);
  if (index === -1) return null;
  return segments[index + 1] || null;
}

export function resolveCampaignVideo(
  url: string | null | undefined,
): CampaignVideoSource {
  const raw = typeof url === 'string' ? url.trim() : '';
  if (!raw) return { kind: 'unsupported', reason: 'No video URL yet.' };

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return { kind: 'unsupported', reason: UNSUPPORTED_VIDEO_REASON };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { kind: 'unsupported', reason: UNSUPPORTED_VIDEO_REASON };
  }

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname.toLowerCase();
  const segments = parsed.pathname.split('/').filter(Boolean);

  if (FILE_EXTENSIONS.some((extension) => path.endsWith(extension))) {
    return { kind: 'file', src: parsed.toString() };
  }

  if (YOUTUBE_HOSTS.includes(host)) {
    const id =
      youtubeId(parsed.searchParams.get('v')) ??
      youtubeId(segmentAfter(segments, 'shorts')) ??
      youtubeId(segmentAfter(segments, 'embed')) ??
      youtubeId(segmentAfter(segments, 'live'));
    if (id) return { kind: 'embed', src: `https://www.youtube.com/embed/${id}`, provider: 'youtube' };
  }

  if (host === 'youtu.be') {
    const id = youtubeId(segments[0]);
    if (id) return { kind: 'embed', src: `https://www.youtube.com/embed/${id}`, provider: 'youtube' };
  }

  if (VIMEO_HOSTS.includes(host)) {
    const afterVideo = segmentAfter(segments, 'video');
    const first = afterVideo || segments[0];
    if (first && /^\d+$/.test(first)) {
      const hashIndex = segments.indexOf(first) + 1;
      const hash = segments[hashIndex];
      const suffix = hash && /^[A-Za-z0-9]+$/.test(hash) ? `?h=${hash}` : '';
      return { kind: 'embed', src: `https://player.vimeo.com/video/${first}${suffix}`, provider: 'vimeo' };
    }
  }

  if (INSTAGRAM_HOSTS.includes(host)) {
    for (const prefix of ['reel', 'reels', 'p', 'tv']) {
      const code = segmentAfter(segments, prefix);
      if (code && /^[A-Za-z0-9_-]+$/.test(code)) {
        return {
          kind: 'embed',
          src: `https://www.instagram.com/${prefix}/${code}/embed`,
          provider: 'instagram',
        };
      }
    }
  }

  return { kind: 'unsupported', reason: UNSUPPORTED_VIDEO_REASON };
}

export function videoPlaysAreCountable(url: string | null | undefined): boolean {
  return resolveCampaignVideo(url).kind === 'file';
}

export function autoplayEmbedSrc(
  source: CampaignVideoSource,
): string | null {
  if (source.kind !== 'embed') return null;

  const params =
    source.provider === 'youtube'
      ? // `playsinline` keeps an iOS start inside the card instead of throwing
        { autoplay: '1', mute: '1', playsinline: '1' }
      : source.provider === 'vimeo'
        ? { autoplay: '1', muted: '1' }
        : null;

  if (!params) return source.src;

  try {
    const url = new URL(source.src);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
    return url.toString();
  } catch {
    return source.src;
  }
}
